import React from 'react';
import { compose, pluck } from 'ramda';
import { func, arrayOf, string } from 'prop-types';
import { connect } from 'react-redux';
import { Switch, Route, Redirect, withRouter } from 'react-router-dom';
import styled, { injectGlobal } from 'styled-components';
import { Flex, Box } from 'grid-styled';

import Paper from 'material-ui/Paper';
import Divider from 'material-ui/Divider';

import PrimaryToolbar from './PrimaryToolbar';
import Population from './Population';
import ViewDropdown from './ViewDropdown';
import TitleBar from './TitleBarContainer';
import Cities from './Cities';
import About from './About';
import News from './News';
import Page from './Page';
import MapPage from './MapPage';
import ChartPage from './ChartPage';
import TablePage from './TablePage';
import QuerySync from './QuerySync';

import { getApiIntervals } from '../selectors/intervals';
import { getSelectedCityId } from '../selectors/router';
import {
  getPrecision,
  getCompare,
  getSafeCrimeType,
} from '../selectors/options';
import { fetchCrimes } from '../reducers/crimes';
import { fetchTimeBounds } from '../reducers/timeBounds';
import { fetchCrimeTypes } from '../reducers/crimeTypes';
import { fetchCities } from '../reducers/cities';
import { Precision as IPrecision, Compare as ICompare } from '../models';
import { getPopulationSetting } from '../reducers/population';
import {
  getLocationsTableSorted,
  getSelectedLocationsInPopulationSubset,
} from '../selectors/locations';
import { selectLocations } from '../actions/options';

import NoContent from './NoContent';

// TODO: refactor background-color, it shouldn't be global
injectGlobal`
  html, body, #root {
    height: 100%;
    font-family: ${({ theme }) => theme.fonts['font-family']};
    font-style: normal;
    font-weight: normal;
    font-size: 17px;
    letter-spacing: 0.01em;
    background-color: ${({ theme }) => theme.colors.grey800};
    color: rgba(0, 0, 0, 0.8);
  }
`;

const Body = styled(Flex)`
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const OnTop = Box.extend`
  flex: 0 0 auto;
  z-index: 10;
`;

const Main = Box.extend`
  flex: 0 1 100%;
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;
`;

const DropHolder = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
  display: flex;
`;

/*
 * TODO: refactor data fetching in favor of simplifying component
 */
class App extends React.Component {
  state = {
    didError: false,
  };

  componentDidMount() {
    const { fetchCities, fetchTimeBounds, fetchCrimeTypes } = this.props;

    Promise.all([fetchTimeBounds(), fetchCities(), fetchCrimeTypes()]).catch(
      this.handleFailedRequest,
    );
  }

  handleFailedRequest = e => this.setState({ didError: true });

  componentWillReceiveProps(nextProps) {
    const {
      precision,
      allIntervals,
      compare,
      selectedCityId,
      crimeType,
    } = this.props;
    if (!allIntervals) return;

    if (
      allIntervals !== nextProps.allIntervals ||
      precision !== nextProps.precision ||
      compare !== nextProps.compare ||
      selectedCityId !== nextProps.selectedCityId ||
      crimeType !== nextProps.crimeType
    ) {
      this.props.fetchCrimes(
        nextProps.selectedCityId,
        nextProps.allIntervals,
        nextProps.precision,
        nextProps.compare,
        nextProps.crimeType,
      );
    }
  }
  componentDidUpdate(prevProps) {
    // Make sure current populations are being selected.
    // TODO: Probably a cleaner way to do this in reducers with thunk?
    const { population, locations, selectLocations } = this.props;
    if (prevProps.population !== population) {
      const allLocations = pluck('id', locations);
      selectLocations(allLocations, allLocations);
    }
  }

  render() {
    return (
      <Body>
        <OnTop>
          <Paper>
            <PrimaryToolbar />
            <Route path="/(map|chart|table)/:cityId?" component={TitleBar} />
          </Paper>
        </OnTop>
        <Main>
          <DropHolder>
            <Route
              path="/(map|chart|table)/:cityId?"
              component={ViewDropdown}
            />
            <Route
              path="/(chart|table)/:cityId?"
              render={props =>
                !props.match.params.cityId ? (
                  <React.Fragment>
                    <Divider />
                    <Population {...props} />
                  </React.Fragment>
                ) : null
              }
            />
          </DropHolder>
          {!this.state.didError ? (
            <Switch>
              <Route exact path="/map/:cityId?" component={MapPage} />
              <Route path="/chart/:cityId?" component={ChartPage} />
              <Route path="/table/:cityId?" component={TablePage} />
              <Route path="/page/:slug" component={Page} />
              <Route path="/about" component={About} />
              <Route path="/cities/:slug?" component={Cities} />
              <Route path="/news" component={News} />
              <Redirect to="/map" from="*" />
            </Switch>
          ) : (
            <NoContent>
              {`Sorry, we couldn't get the data.
              Please try again later`}
            </NoContent>
          )}
        </Main>
        <QuerySync />
      </Body>
    );
  }
}

App.propTypes = {
  selectedCityId: string,
  fetchCrimes: func.isRequired,
  fetchCities: func.isRequired,
  fetchTimeBounds: func.isRequired,
  fetchCrimeTypes: func.isRequired,
  precision: IPrecision.isRequired,
  compare: ICompare.isRequired,
  allIntervals: arrayOf(string),
};

export default compose(
  withRouter,
  connect(
    state => ({
      selectedCityId: getSelectedCityId(state),
      precision: getPrecision(state),
      crimeType: getSafeCrimeType(state),
      compare: getCompare(state),
      allIntervals: getApiIntervals(state),
      locations: getLocationsTableSorted(state),
      selectedLocationsIds: getSelectedLocationsInPopulationSubset(state),
      population: getPopulationSetting(state),
    }),
    {
      fetchCrimes,
      fetchCities,
      fetchTimeBounds,
      fetchCrimeTypes,
      selectLocations,
    },
  ),
)(App);



// WEBPACK FOOTER //
// ./src/components/App.jsx